//#region Library imports
//#endregion
//#region Helper imports
import { clsx } from '@alexis/helpers/clsx';
//#endregion
//#region Type imports
import React, { forwardRef, useCallback, useState } from 'react';

//#endregion
//#region SVG imports
import CircleQuestionIcon from '@icons/circle_question.svg';
import CircleTickIcon from '@icons/circle_tick.svg';
import CreditCardIcon from '@icons/credit_card.svg';
import WarningIcon from '@icons/warning.svg';

//#endregion
import styles from './Input.module.scss';

type InputProps = {
  /**
   * @variation email
   * @variation off
   * @variation on
   * @variation street-address
   * @variation username
   */
  autoComplete?: string;
  className?: string;
  hasError?: boolean;
  id?: string;
  inputMode?: 'none' | 'text' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | 'search';
  isCreditCard?: boolean;
  isRtl: boolean;
  isValidated: boolean;
  maxLength?: number;
  message?: string;
  name?: string;
  onBlur?(): void;
  onChange(value: string): void;
  onHelp?(): void;
  placeholder: string;
  style?: React.CSSProperties;
  type?: string;
  value?: string | string[] | number;
  withInfo?: boolean;
};

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      autoComplete,
      className,
      hasError,
      id,
      inputMode,
      isCreditCard,
      isRtl,
      isValidated,
      maxLength,
      message,
      name,
      onBlur,
      onChange,
      onHelp,
      placeholder,
      style,
      type,
      value,
      withInfo,
    },
    ref,
  ): JSX.Element => {
    const [isFocus, setIsFocus] = useState<boolean>(false);

    const handleOnBlur = useCallback<() => void>(() => {
      setIsFocus(false);

      if (!!onBlur) {
        onBlur();
      }
    }, [onBlur]);

    const defaultInputId = 'inputId';

    return (
      <div
        className={clsx(
          styles.container,
          className,
          isRtl && styles.rtl,
          isFocus && styles.focus,
          isValidated && styles.validated,
          !!hasError && styles.error,
        )}
        style={style}
      >
        <div className={styles.inputContainer}>
          <input
            autoComplete={autoComplete}
            id={id ?? defaultInputId}
            inputMode={inputMode}
            name={name}
            onBlur={handleOnBlur}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
            onFocus={() => setIsFocus(true)}
            ref={ref}
            spellCheck={false}
            type={type}
            value={value}
          />
          <label htmlFor={id ?? defaultInputId}>{placeholder}</label>

          {isValidated && !hasError ? (
            <CircleTickIcon className={clsx(styles.icon, styles.validation)} />
          ) : null}

          {isValidated && !!hasError ? (
            <WarningIcon className={clsx(styles.icon, styles.validation)} />
          ) : null}

          {!!withInfo && !isValidated ? (
            <CircleQuestionIcon className={styles.icon} onClick={onHelp} />
          ) : null}

          {!!isCreditCard && !isValidated ? <CreditCardIcon className={styles.icon} /> : null}
        </div>

        <div className={styles.messageContainer}>
          <div className={styles.message}>{message}</div>

          {!!maxLength && maxLength > 0 ? (
            <div>
              {!!value ? value.toString().trim().length : 0}/{maxLength}
            </div>
          ) : null}
        </div>
      </div>
    );
  },
);

export default Input;
